import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { fetchCurrentUser } from "../api/userService";
import { usePermissions } from "./usePermissions";
import { usePublisherBySourceId } from "./usePublisherBySourceId";
/**
 * sourceId of the logged-in user's own organization: the active one, else the
 * first one (`_self` lists organizations directly, see mod-br-users OrganizationDto).
 *
 * @param user the `_self` response
 */
export var ownOrganizationSourceId = function (user) {
    var _a, _b;
    var organization = (_a = user === null || user === void 0 ? void 0 : user.activeOrganization) !== null && _a !== void 0 ? _a : (_b = user === null || user === void 0 ? void 0 : user.userOrganizations) === null || _b === void 0 ? void 0 : _b[0];
    return (organization === null || organization === void 0 ? void 0 : organization.sourceId) || (organization === null || organization === void 0 ? void 0 : organization.id) || undefined;
};
/**
 * A list opened without filters shows a publisher user their own records.
 * Until that is decided the list must not search — a search without the
 * publisher would be thrown away. The decision waits only for what it needs:
 * the roles (one request, also read from sessionStorage), and only for a user
 * the auto-fill applies to the own organization and its publisher, each
 * without retries. Any failure decides "no auto-fill" so the list is never
 * stuck; a failed user lookup is reported in `isError`.
 *
 * @returns `isPending` while undecided, `isError` when the user or their roles
 *   could not be loaded, `cancel` to decide "no auto-fill" (the user applied or
 *   cleared filters themselves)
 */
export var usePublisherAutoFill = function (_a) {
    var appliesTo = _a.appliesTo, hasFilterParams = _a.hasFilterParams, onAutoFill = _a.onAutoFill, canAutoFill = _a.canAutoFill;
    var ky = useOkapiKy();
    var history = useHistory();
    var location = useLocation();
    var _b = usePermissions(), activeRoles = _b.activeRoles, activeRolesError = _b.activeRolesError;
    var rolesError = !!activeRolesError;
    var _c = useState(function () {
        return hasFilterParams(location.search);
    }), isDecided = _c[0], setIsDecided = _c[1];
    // the user's organization could not be loaded when it was needed
    var _d = useState(false), userLoadFailed = _d[0], setUserLoadFailed = _d[1];
    // Filters arriving in the URL decide it in an effect, together with the
    // list's own URL effect: deciding in render would search once more with the
    // filters the list held before.
    var urlHasFilters = hasFilterParams(location.search);
    var rolesRuleOut = activeRoles !== undefined && !appliesTo(activeRoles);
    var isOpen = isDecided || rolesRuleOut || rolesError;
    // Only a user the auto-fill applies to waits for their organization.
    var needsOwnPublisher = !isOpen && activeRoles !== undefined;
    var currentUser = useQuery(["currentUser"], function () { return fetchCurrentUser(ky); }, { enabled: needsOwnPublisher, staleTime: Infinity, retry: false });
    var sourceId = needsOwnPublisher
        ? ownOrganizationSourceId(currentUser.data)
        : undefined;
    var ownPublisher = usePublisherBySourceId(sourceId, false);
    // Another observer of the same query may have started it with retries: the
    // first failure already decides, the list does not wait for the retries.
    var userFailed = currentUser.isError || currentUser.failureCount > 0;
    var userLoading = currentUser.isLoading && !userFailed;
    var publisherLoading = ownPublisher.isLoading && ownPublisher.failureCount === 0;
    // The callbacks are recreated on every render; the effect reads the latest.
    var callbacksRef = useRef({ hasFilterParams: hasFilterParams, onAutoFill: onAutoFill, canAutoFill: canAutoFill });
    callbacksRef.current = { hasFilterParams: hasFilterParams, onAutoFill: onAutoFill, canAutoFill: canAutoFill };
    // StrictMode runs a mount effect twice: the URL must be pushed once.
    var appliedRef = useRef(false);
    useEffect(function () {
        var _a, _b, _c, _d;
        var callbacks = callbacksRef.current;
        if (isDecided)
            return;
        if (isOpen || urlHasFilters) {
            // the URL, the roles or a failure decided it — for good
            setIsDecided(true);
            return;
        }
        if (!needsOwnPublisher || userLoading)
            return;
        if (sourceId && publisherLoading)
            return;
        if (!appliedRef.current) {
            appliedRef.current = true;
            if (userFailed)
                setUserLoadFailed(true);
            var publisherSourceId = (_b = (_a = ownPublisher.data) === null || _a === void 0 ? void 0 : _a.sourceId) !== null && _b !== void 0 ? _b : sourceId;
            if (sourceId &&
                ownPublisher.data &&
                publisherSourceId &&
                // the URL or the filter pane may have changed while the lookup ran
                !callbacks.hasFilterParams(history.location.search) &&
                ((_d = (_c = callbacks.canAutoFill) === null || _c === void 0 ? void 0 : _c.call(callbacks)) !== null && _d !== void 0 ? _d : true)) {
                callbacks.onAutoFill(publisherSourceId);
            }
        }
        setIsDecided(true);
    }, [
        isDecided,
        isOpen,
        urlHasFilters,
        needsOwnPublisher,
        userLoading,
        sourceId,
        publisherLoading,
        ownPublisher.data,
        userFailed,
        history,
    ]);
    var cancel = useCallback(function () { return setIsDecided(true); }, []);
    return {
        isPending: !isOpen,
        isError: rolesError || userLoadFailed,
        cancel: cancel,
    };
};
